/**
 * bankMatching.js — Banka hareketi eşleşme havuzu, React Query hook'ları.
 *
 * Backend referansı: backend/routes/bank_transactions.py (bu turda backend
 * DEĞİŞTİRİLMEDİ, salt okundu — services/bank_transaction_service.py +
 * services/matching_engine.py). Tüm uçlar admin JWT gerektirir, `/api`
 * prefix'i apiClient baseURL'inde zaten var.
 *
 * Hooks:
 *   useUnmatchedBankTransactions(params) — GET /admin/bank-transactions/unmatched
 *                                           (bank_id/direction/date_from/date_to filtresi)
 *   useMatchBankTransaction()            — POST /admin/bank-transactions/{txn_id}/match
 *   useIgnoreBankTransaction()           — DELETE /admin/bank-transactions/{txn_id}
 *                                           (soft-delete — "Yoksay" aksiyonu)
 *   useTriggerAutoMatch()                — POST /admin/bank-transactions/auto-match
 *
 * NOT (sözleşme — bkz. bank_transaction_service.py + matching_engine.py):
 * `unmatched` uçtan dönen her kayıtta `suggested_payment_id` / `suggested_payment_ref`
 * / `match_confidence` / `match_method` alanları YALNIZCA daha önce "Otomatik Eşleştir"
 * çalıştırılmışsa (cron veya bu ekrandaki buton) dolu gelir — GET isteği kendiliğinden
 * skorlama YAPMAZ. Öneri alanları boşsa admin önce "Otomatik Eşleştir"i tetiklemeli.
 *
 * Öneri satırında öğrenci adı YOKTUR (backend bunu döndürmüyor) — bkz.
 * BANKA_ESLESME_HAVUZU_EKSIK_UCLAR.md / worktree raporu "EKSİK backend uçları".
 * Kart bileşeni bu yüzden yalnızca payment_ref + confidence + method gösterir,
 * admin karşılaştırmayı hareketin gönderen adı/açıklaması ile elle yapar.
 *
 * Tur 2 (2026-09-29, feature/parasut-fatura-faz4-web) — YUKARIDAKİ NOT
 * KISMEN GÜNCEL DEĞİL: backend `unmatched` yanıtına
 * `suggested_user_id`/`suggested_student_name`/`suggested_course_title`/
 * `suggested_amount`/`suggested_due_date` alanlarını EKLEDİ — `BankMatchingCard.jsx`
 * bu alanları VARSA kullanır, YOKSA eski (yalnızca payment_ref) görünüme düşer.
 *
 * Sözleşme güncellemesi (aynı gün, koordinatör mesajı — madde C) — yeni satış
 * akışında AÇIK TAKSİTLERİN pending `payments` kaydı YOK; backend taksite
 * DOĞRUDAN eşleştirme ekliyor:
 *   - `match` body'si ARTIK `{payment_id}` VEYA `{installment_id}` (İKİSİ
 *     BİRDEN → 400 `payment_id_ve_installment_id_birlikte_verilemez`) —
 *     bkz. `useMatchBankTransaction` docstring'i.
 *   - `unmatched` öğelerine `match_type` ("payment"|"installment") +
 *     `suggested_installment_id` eklendi (`suggested_payment_id`'nin
 *     yanında) — "Önerileni eşleştir" `match_type`'a göre doğru id'yi
 *     gönderir (bkz. BankMatchingCard.jsx).
 *   - Taksit yoluyla eşleştirmede tutar taksitle TAM eşleşmeli — uyuşmazsa
 *     400 `tutar_uyusmuyor`; kart tek çekim (method=credit_card) taksitler
 *     400 `taksit_uygun_degil`. Backend her ikisi için de `message_tr`/
 *     `message_en` sağlıyor — `getApiErrorMessage` bunu OLDUĞU GİBİ gösterir,
 *     özel bir kod dalı GEREKMEZ.
 *
 * Tur 3 (aynı gün, backend-teyit turu) — `GET /admin/bank-transactions/matched`
 * ve yukarıdaki tüm alan adları backend worktree'sinde (routes/
 * bank_transactions.py, services/bank_transaction_service.py) salt okunarak
 * DOĞRULANDI — hepsi HAZIR, kontrat birebir eşleşiyor.
 *
 *   useMatchedBankTransactions(params)   — GET /admin/bank-transactions/matched (HAZIR)
 *
 * Tur 4 (elle test bulguları, ⚠️ backend bu turda YAZIYOR, bu dosyada
 * doğrulanmadı) — `unmatched` öğelerine `name_match` eklenecek: `null` VEYA
 * `{user_id, student_name, open_items:[{match_type, payment_id|null,
 * installment_id|null, amount, due_date, course_title, installment_no}]}`.
 * Gönderen adı serbest metinle bir öğrenciyle eşleşirse doldurulur —
 * `suggested_*` öneri motorundan BAĞIMSIZ ikinci bir kaynak (`BankMatchingCard.jsx`
 * ikisini karşılaştırıp `name_match.user_id !== suggested_user_id` iken
 * uyarı gösterir). Bu hook'ta EK bir alan/parametre GEREKMEZ — `name_match`
 * zaten `unmatched` yanıtının bir parçası olarak gelecek, `useUnmatchedBank
 * Transactions` aynen geçirir.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/apiClient';
import { useAuth } from '@/contexts/AuthContext';

export const bankMatchingKeys = {
  unmatched: (params) => ['admin', 'bank-transactions', 'unmatched', params || {}],
  matched: (params) => ['admin', 'bank-transactions', 'matched', params || {}],
};

// ── useUnmatchedBankTransactions ──────────────────────────────────────────────

/**
 * @param {{bankId?: string, direction?: string, dateFrom?: string, dateTo?: string, skip?: number, limit?: number}} params
 * @returns `{ items: [], total, has_more }`
 */
export function useUnmatchedBankTransactions(params = {}) {
  const { authHeaders } = useAuth();
  const { bankId, direction, dateFrom, dateTo, skip = 0, limit = 20 } = params;
  const queryParams = {
    bank_id: bankId || undefined,
    direction: direction || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    skip,
    limit,
  };
  return useQuery({
    queryKey: bankMatchingKeys.unmatched(queryParams),
    queryFn: async () => {
      const res = await apiClient.get('/admin/bank-transactions/unmatched', {
        headers: authHeaders(),
        params: queryParams,
      });
      return res.data || { items: [], total: 0, has_more: false };
    },
  });
}

// ── useMatchedBankTransactions ("Eşleşenler" sekmesi) ─────────────────────────

/**
 * `GET /admin/bank-transactions/matched` — backend HAZIR (Tur 3'te
 * routes/bank_transactions.py + services/bank_transaction_service.py::
 * list_matched salt okunarak doğrulandı, kontratla birebir eşleşiyor).
 * `{ items: [{txn_id,date,amount,sender_name,description,bank_id,
 * matched_payment_id,user_id,student_name,course_title,matched_at,
 * matched_by}], total }`. `retry:false` savunma amaçlı korunur (gerçek bir
 * ağ hatasında `isError` true olur, `AdminBankMatchingPool.jsx` mevcut
 * hata/tekrar-dene UI'siyle gösterir, sayfa ÇÖKMEZ).
 *
 * @param {{dateFrom?: string, dateTo?: string, skip?: number, limit?: number}} params
 */
export function useMatchedBankTransactions(params = {}) {
  const { authHeaders } = useAuth();
  const { dateFrom, dateTo, skip = 0, limit = 20 } = params;
  const queryParams = {
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    skip,
    limit,
  };
  return useQuery({
    queryKey: bankMatchingKeys.matched(queryParams),
    queryFn: async () => {
      const res = await apiClient.get('/admin/bank-transactions/matched', {
        headers: authHeaders(),
        params: queryParams,
      });
      return res.data || { items: [], total: 0 };
    },
    retry: false,
  });
}

// ── useMatchBankTransaction ───────────────────────────────────────────────────

/**
 * `mutateAsync({ transactionId, paymentId })` VEYA `mutateAsync({ transactionId, installmentId })`
 * — İKİSİ BİRDEN VERİLMEZ (backend 400 `payment_id_ve_installment_id_birlikte_
 * verilemez` döner). `installmentId` verildiğinde taksit KAPANIR (bağlı
 * senet dahil) — tutar taksitle TAM eşleşmeli (400 `tutar_uyusmuyor`), kart
 * tek çekim taksitler kabul edilmez (400 `taksit_uygun_degil`). Backend
 * (Tur 3'te salt okundu) HAZIR ve kontratla birebir eşleşiyor.
 */
export function useMatchBankTransaction() {
  const { authHeaders } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ transactionId, paymentId, installmentId }) =>
      apiClient
        .post(
          `/admin/bank-transactions/${transactionId}/match`,
          installmentId ? { installment_id: installmentId } : { payment_id: paymentId },
          { headers: authHeaders() },
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'bank-transactions'] });
    },
  });
}

// ── useIgnoreBankTransaction (soft-delete — "Yoksay") ─────────────────────────

/** `mutateAsync(transactionId)` */
export function useIgnoreBankTransaction() {
  const { authHeaders } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (transactionId) =>
      apiClient
        .delete(`/admin/bank-transactions/${transactionId}`, { headers: authHeaders() })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'bank-transactions'] });
    },
  });
}

// ── useTriggerAutoMatch ────────────────────────────────────────────────────────

/** `mutateAsync(limit?)` → `{ total_processed, auto_approved, suggestions_added, skipped, no_match, errors }` */
export function useTriggerAutoMatch() {
  const { authHeaders } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (limit) =>
      apiClient
        .post('/admin/bank-transactions/auto-match', limit ? { limit } : {}, { headers: authHeaders() })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'bank-transactions'] });
    },
  });
}
